import { Box, Typography, darken, useMediaQuery } from "@mui/material";
import { Callout } from "../Callout";
import { RED_NEGATIVE } from "@helpers/incomeProfileHelpers";
import { formatCurrencyWhole } from "@helpers/funciones";

interface Props {
  totalIncome: number;
  totalExpenses: number;
}

const amountSx = {
  fontFamily: "Outfit",
  fontSize: 19,
  fontWeight: 600,
  letterSpacing: "-0.01em",
  fontVariantNumeric: "tabular-nums",
} as const;

const Operator = ({ children }: { children: string }) => (
  <Typography
    sx={{ display: "grid", placeItems: "center", fontFamily: "Outfit", fontSize: 15, color: "tokens.textTertiary" }}
  >
    {children}
  </Typography>
);

const Amount = ({ label, value }: { label: string; value: number }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: 0.5,
      p: "13px 15px",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: "10px",
    }}
  >
    <Typography variant="body3" color="tokens.textTertiary">
      {label}
    </Typography>
    <Typography sx={[amountSx, { color: "primary.dark" }]}>{formatCurrencyWhole(value)}</Typography>
  </Box>
);

/**
 * Replica `.mc-util` del perfil de ingresos: Ingresos − Egresos = Utilidad con su margen. La utilidad
 * va en el tono de marca, o en gris y rojo con pérdida; si los egresos igualan o superan a los
 * ingresos aparece el aviso de capacidad de pago. Abajo de 1000px se apila en una columna.
 */
export const ProfitSummary = ({ totalIncome, totalExpenses }: Props) => {
  const isNarrow = useMediaQuery("(max-width:1000px)");
  const profit = totalIncome - totalExpenses;
  const margin = totalIncome > 0 ? (profit / totalIncome) * 100 : 0;
  const isLoss = profit < 0;

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isNarrow ? "1fr" : "1fr 22px 1fr 22px 1.15fr",
          alignItems: "stretch",
          gap: isNarrow ? "6px" : "8px",
          mt: 1,
          mb: "18px",
        }}
      >
        <Amount label="Ingresos" value={totalIncome} />
        {!isNarrow && <Operator>−</Operator>}
        <Amount label="Egresos" value={totalExpenses} />
        {!isNarrow && <Operator>=</Operator>}
        {/* `.mc-util-r` */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 0.5,
            p: "13px 15px",
            border: "1px solid",
            borderRadius: "10px",
            borderColor: isLoss ? "tokens.borderStrong" : "primary.main",
            bgcolor: (theme) =>
              isLoss
                ? theme.palette.tokens?.surface2
                : `color-mix(in oklab, ${theme.palette.primary.main} 7%, transparent)`,
          }}
        >
          <Typography variant="body3" color="tokens.textTertiary">
            Utilidad
          </Typography>
          <Typography
            sx={[amountSx, { color: (theme) => (isLoss ? RED_NEGATIVE : darken(theme.palette.primary.main, 0.25)) }]}
          >
            {formatCurrencyWhole(profit)}
          </Typography>
          <Typography variant="body3" color="tokens.textTertiary">
            {margin.toFixed(0)}% de margen
          </Typography>
        </Box>
      </Box>

      {profit <= 0 && totalIncome > 0 && (
        <Callout tone="warn">
          Con las cifras capturadas tus egresos igualan o superan tus ingresos. Revisa los montos antes de continuar:
          así como está, la solicitud no pasaría el análisis de capacidad de pago.
        </Callout>
      )}
    </>
  );
};
